import { useFormContext } from 'react-hook-form'
import { IntegrationSettings } from '../../../schema'
import { Col, Row } from 'react-bootstrap'
import { SelectDropdown, SelectOption } from '../../../../../../../components/SelectDropdown/SelectDropdown'
import { getIntegrationDisplayName, IntegrationServiceId } from '../../../../../../../common/integrations'
import { DetailedCRMAccountIntegrationMapping, MappingType } from '../../../../../../../models/Account'
import { useFilterIntegrationMappingTemplatesQuery, useGetSupportedObjectsQuery } from '../../../../../../../store/integrations/integrations'
import { useCallback, useMemo } from 'react'
import { selectAccountId } from '../../../../../../../store/auth/authSlice'
import { useAppSelector } from '../../../../../../../store/hooks'
import { useGetIntegrationAdminSettings } from '../../../hook'

interface SelectContactTypeSectionProps {
  onChange: () => void
  serviceId: IntegrationServiceId
  disabled?: boolean
}

export const SelectContactTypeSection: React.FC<SelectContactTypeSectionProps> = ({ onChange, serviceId, disabled }) => {
  const {
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<IntegrationSettings>()
  const accountId = useAppSelector(selectAccountId)
  const currentObjectName = watch('mappings.contact.objectName')
  const currentMappingType = watch('mappings.contact.mappingType')

  const isCustom = useMemo(() => currentMappingType === MappingType.CUSTOM, [currentMappingType])

  const { data: inboundMappingTemplatePaths, isFetching: isInboundMappingTemplatePathsFetching } = useFilterIntegrationMappingTemplatesQuery({
    active: true,
    sourcePlatform: serviceId,
    destinationObject: 'contact',
  })

  const sessionLinkings = watch('mappings.session.contactLinks')
  const sessionFilters = watch('mappings.session.filters')

  const taskLinkings = watch('mappings.task.contactLinks')
  const taskFilters = watch('mappings.task.filters')

  const additionalObjectsMappings = watch('mappings.additionalObjects') as DetailedCRMAccountIntegrationMapping[]

  const clearContactLinksIfNeeded = useCallback(() => {
    if ((sessionLinkings && sessionLinkings.length > 0) || (sessionFilters && sessionFilters.length > 0)) {
      setValue('mappings.session.contactLinks', [], { shouldDirty: true })
      setValue('mappings.session.filters', [], { shouldDirty: true })
    }
    if ((taskLinkings && taskLinkings.length > 0) || (taskFilters && taskFilters.length > 0)) {
      setValue('mappings.task.contactLinks', [], { shouldDirty: true })
      setValue('mappings.task.filters', [], { shouldDirty: true })
    }
    if (additionalObjectsMappings && additionalObjectsMappings.length > 0) {
      const newAdditionalObjects = additionalObjectsMappings.map((obj: DetailedCRMAccountIntegrationMapping) => ({
        ...obj,
        contactLinks: [],
        filters: [
          obj.filters?.map((f) => ({
            ...f,
            groupOperator: f.groupOperator,
            expressions: f.expressions.filter((e) => e.field !== 'ContactId' && e.source.field !== 'contactId'),
          })) || [],
        ].flat(),
      }))
      setValue('mappings.additionalObjects', newAdditionalObjects, { shouldDirty: true })
    }
  }, [setValue, sessionLinkings, sessionFilters, taskLinkings, taskFilters, additionalObjectsMappings])

  const contactCRMTypeOptions: SelectOption[] = useMemo(() => {
    if (!inboundMappingTemplatePaths) return []
    return inboundMappingTemplatePaths.map((i) => ({ id: i.sourceObject, label: i.name }))
  }, [inboundMappingTemplatePaths])

  const { hasIntegration } = useGetIntegrationAdminSettings(serviceId)

  const { data: supportedObjects, isLoading: isLoadingSupportedObjects } = useGetSupportedObjectsQuery(
    { accountId, connector: serviceId },
    { skip: !accountId || !serviceId || !hasIntegration },
  )

  const customContactOption = useMemo(() => supportedObjects?.find((opt) => opt.name === currentObjectName) || null, [supportedObjects, currentObjectName])
  const contactTypeSelectOptions: SelectOption[] = useMemo(
    () => supportedObjects?.map((obj) => ({ id: obj.name, label: `${obj.label} (${obj.name})` })) || [],
    [supportedObjects],
  )

  const predefinedContactOption: SelectOption | null = useMemo(() => {
    if (isCustom) {
      return { id: 'customContactObject', label: 'Custom Contact Object' }
    }
    return contactCRMTypeOptions.find((opt) => opt.id === currentObjectName) || null
  }, [contactCRMTypeOptions, currentObjectName, isCustom])
  const handleCustomOptionSelect = (option: { id: string; label: string }) => {
    if (option.id === currentObjectName && isCustom) {
      return
    }
    clearContactLinksIfNeeded()
    setValue('mappings.contact', { objectName: option.id }, { shouldDirty: true, shouldValidate: true })
    setValue('mappings.contact.mappingType', MappingType.CUSTOM, {
      shouldDirty: true,
    })
    onChange()
  }

  const handlePredefinedOptionSelect = (option: { id: string; label: string }) => {
    if (option.id === currentObjectName && !isCustom) {
      return
    }
    clearContactLinksIfNeeded()
    if (option.id === 'customContactObject') {
      setValue('mappings.contact', { objectName: '' }, { shouldDirty: true, shouldValidate: true })
      setValue('mappings.contact.mappingType', MappingType.CUSTOM, {
        shouldDirty: true,
        shouldValidate: true,
      })
      onChange()
      return
    }
    setValue('mappings.contact', { objectName: option.id }, { shouldDirty: true, shouldValidate: true })
    setValue('mappings.contact.mappingType', MappingType.PREDEFINED, {
      shouldDirty: true,
      shouldValidate: true,
    })
    onChange()
  }

  const fieldError = errors.mappings?.contact?.objectName
  const objectError = errors.mappings?.contact

  return (
    <>
      <Row className='mb-2'>
        <Col>
          <h4>{getIntegrationDisplayName(serviceId)} contact object*</h4>
          <p className='zocks-text-gray-600'>Select your {getIntegrationDisplayName(serviceId)} contact object type.</p>
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <SelectDropdown
            initialSelectedOption={predefinedContactOption}
            options={contactCRMTypeOptions}
            onOptionSelected={handlePredefinedOptionSelect}
            loading={isInboundMappingTemplatePathsFetching}
            disabled={disabled}
          />
          {fieldError && <div className='text-danger mt-1 small'>{fieldError.message}</div>}
          {objectError && <div className='text-danger mt-1 small'>{objectError.message}</div>}
        </Col>
        {isCustom && (
          <Col md={6}>
            <SelectDropdown
              options={contactTypeSelectOptions}
              initialSelectedOption={
                customContactOption ? { id: customContactOption.name, label: `${customContactOption.label} (${customContactOption.name})` } : null
              }
              placeholder='Select Object Type'
              onOptionSelected={handleCustomOptionSelect}
              searchable
              loading={isLoadingSupportedObjects}
              disabled={disabled}
            />
          </Col>
        )}
      </Row>
    </>
  )
}
